import { enqueueTask, restartableTask } from 'ember-concurrency';
import groupBy from 'lodash/groupBy';
import PoTask from 'volta/models/po-task';
import { SkuResourceName } from 'volta/models/sku';
import SkuTask from 'volta/models/sku-task';
import {
  SKU_TASK_TYPES,
  TaskResourceName,
  TaskResourceNameFromParentRecordResourceName,
  taskToCommand
} from 'volta/models/task';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter, inFilter, niFilter } from 'volta/utils/filters-utils';
import { isPresent } from '@ember/utils';
import { formatLocalDate } from "volta/utils/date-utils";

import Service, { service } from '@ember/service';
import SimulationTask from 'volta/models/simulation-task';
import SimulationItemSettingTask from 'volta/models/simulation-item-setting-task';
import { SimulationResourceName } from 'volta/models/simulation';
import { SimulationItemSettingResourceName } from 'volta/models/simulation-item-setting';

import type Task from 'volta/models/task';
import type { IResourceName } from 'volta/models/base-model';
import type SessionUserService from './session-user';
import type { TError } from 'volta/utils/error-utils';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { TAssignTo, TTaskType, UTask } from 'volta/models/types/tasks';
import type StoreService from 'volta/services/store';

const DEFAULT_INCLUDE: string = 'createdBy,updatedBy,archivedBy,resolvedBy,assignees';
const DEFAULT_SORT: string = 'dueDate,resolvedAt';

type FetchTaskQuery = {
  assigneeIds?: string[];
  createdById?: string;
  isAssigned?: boolean;
  assigneeIdsToExclude?: string[];
  entitiesId?: string[];
  taskType?: TTaskType[];
};

export default class TaskService extends Service {
  @service store!: StoreService;
  @service sessionUser!: SessionUserService;

  get isSaving() {
    return this.saveTask.isRunning;
  }

  fetchTasks = enqueueTask(async (query: IJsonApiQuery = {}) => {
    if (!query.include) {
      query.include = DEFAULT_INCLUDE;
    }

    if (!query.sort) {
      query.sort = DEFAULT_SORT;
    }

    if (!query.page) {
      query.page = {
        offset: 0,
        limit: 50
      };
    }

    try {
      // @ts-ignore
      return await this.store.query<Task>('task', query);
    } catch (error) {
      defaultErrorHandler(error as TError);
      throw error;
    }
  });

  // MAIN FETCH TASK
  fetchTasksTask = restartableTask(
    async (
      modelName: IResourceName,
      entityId: string,
      taskTypes: TTaskType[],
      include: string = DEFAULT_INCLUDE,
      sort: string = DEFAULT_SORT
    ) => {
      try {
        const tasksQuery: IJsonApiQuery = {
          page: {
            offset: 0,
            limit: 300
          },
          sort,
          include,
          filter: {
            taskType: inFilter(taskTypes)
          }
        };

        const response = await this.store.nestedQuery(
          modelName,
          entityId,
          TaskResourceName,
          tasksQuery
        );

        const resourceName = TaskResourceNameFromParentRecordResourceName(modelName);
        if (resourceName) {
          return normalize(resourceName.singular, response) as UTask[];
        }

        return normalize('task', response) as UTask[];
      } catch (error) {
        defaultErrorHandler(error as TError);
        throw error;
      }
    }
  );

  // SPECIALIZED FETCHES
  fetchPlanningTasks = restartableTask(async (skuId: string) => {
    return this.fetchTasksTask.perform(SkuResourceName, skuId, ['PLANNING_TASK']);
  });

  fetchExecutionTasks = restartableTask(async (skuId: string) => {
    return this.fetchTasksTask.perform(SkuResourceName, skuId, ['EXECUTION_TASK']);
  });

  fetchCommentTasks = restartableTask(async (entityId: string, resourceName: IResourceName) => {
    return this.fetchTasksTask.perform(
      resourceName,
      entityId,
      ['COMMENT' as TTaskType],
      'createdBy',
      'createdAt'
    );
  });

  fetchSkuTasks = restartableTask(async (entityId: string, includeComments: boolean = false) => {
    const taskTypes: TTaskType[] = ['SKU_TASK'];
    if (includeComments) {
      taskTypes.push('COMMENT');
    }
    return this.fetchTasksTask.perform(SkuResourceName, entityId, taskTypes);
  });

  fetchForecastsTasks = restartableTask(async (entityId: string) => {
    const taskTypes: TTaskType[] = ['FORECAST_TASK'];
    return this.fetchTasksTask.perform(SkuResourceName, entityId, taskTypes);
  });

  fetchSimulationTasks = restartableTask(async (entityId: string) => {
    return this.fetchTasksTask.perform(SimulationResourceName, entityId, [
      'SIMULATION_TASK' as TTaskType
    ]);
  });

  fetchSimulationItemSettingTasks = restartableTask(async (entityId: string) => {
    return this.fetchTasksTask.perform(SimulationItemSettingResourceName, entityId, [
      'SIMULATION_ITEM_TASK' as TTaskType
    ]);
  });

  fetchSkusTasks = enqueueTask(async (fetchQuery: FetchTaskQuery = {}) => {
    try {
      if (!fetchQuery.taskType) {
        fetchQuery.taskType = SKU_TASK_TYPES;
      }
      const tasksQuery: IJsonApiQuery = {
        page: {
          offset: 0,
          limit: 20
        },
        filter: {
          ...TaskService.queryFiltersToApiFilters(fetchQuery),
          isResolved: eqFilter(false)
        },
        include: DEFAULT_INCLUDE,
        sort: 'dueDate'
      };

      const response = await this.store.customQuery(
        SkuResourceName.singular,
        TaskResourceName.plural,
        undefined,
        tasksQuery
      );

      return normalize('sku-task', response) as UTask[];
    } catch (error) {
      defaultErrorHandler(error as TError);
      throw error;
    }
  });

  // CRUD METHODS
  saveTask = enqueueTask(async (task: UTask) => {
    try {
      if (task.get('hasDirtyAttributes')) {
        const payload = taskToCommand(task);
        const TaskModel = TaskService.TaskModel(task);

        if (TaskModel) {
          return task.id
            ? // @ts-ignore
            ((await TaskModel.updateTask(payload)) as UTask)
            : // @ts-ignore
            ((await TaskModel.createTask(payload)) as UTask);
        }
      }

      return task;
    } catch (error) {
      defaultErrorHandler(error as TError);
      throw error;
    }
  });

  resolveTask = enqueueTask(async (task: UTask) => {
    return this.onResolve.perform(task, false);
  });

  reopenTask = enqueueTask(async (task: UTask) => {
    return this.onResolve.perform(task, true);
  });

  onResolve = enqueueTask(async (task: UTask, isResolved: boolean) => {
    try {
      if (task.get('hasDirtyAttributes')) {
        const payload = {
          taskId: task.id,
          linkedEntityId: task.linkedEntityId
        };
        const TaskModel = TaskService.TaskModel(task);
        if (TaskModel) {
          return isResolved
            ? // @ts-ignore
            ((await TaskModel?.reopenTask(payload)) as UTask)
            : // @ts-ignore
            ((await TaskModel?.resolveTask(payload)) as UTask);
        }
      }
      return task;
    } catch (error) {
      defaultErrorHandler(error as TError);
      throw error;
    }
  });

  deleteTask = enqueueTask(async (task: UTask) => {
    try {
      const payload = taskToCommand(task);
      const TaskModel = TaskService.TaskModel(task);

      if (task.id) {
        if (TaskModel) {
          // @ts-ignore
          const savedTask = (await TaskModel.deleteTask(payload)) as UTask;
          task.deleteRecord();
          return savedTask;
        }
      } else {
        task.deleteRecord();
      }
      return task;
    } catch (error) {
      defaultErrorHandler(error as TError);
      throw error;
    }
  });

  /**
   * Returns a query filter that can be used to filter tasks
   * @param assignTo AssignTo is a string that can be 'assignments', 'assignings' or 'unassigned'
   * @returns A query filter that can be used to filter tasks
   */
  assignQueryFilter(assignTo?: TAssignTo): FetchTaskQuery {
    switch (assignTo) {
      case 'assignments': {
        return { assigneeIds: [this.sessionUser.userId] };
      }
      case 'assignings': {
        return {
          createdById: this.sessionUser.userId,
          assigneeIdsToExclude: [this.sessionUser.userId],
          isAssigned: true
        };
      }
      case 'unassigned': {
        return { createdById: this.sessionUser.userId, isAssigned: false };
      }
      default: {
        return {};
      }
    }
  }

  private static TaskModel(task: UTask) {
    let TaskModel:
      | typeof PoTask
      | typeof SkuTask
      | typeof SimulationTask
      | typeof SimulationItemSettingTask
      | undefined;

    if (task instanceof SkuTask) {
      TaskModel = SkuTask;
    } else if (task instanceof SimulationTask) {
      TaskModel = SimulationTask;
    } else if (task instanceof SimulationItemSettingTask) {
      TaskModel = SimulationItemSettingTask;
    } else {
      TaskModel = PoTask;
    }
    return TaskModel;
  }

  /**
   * Returns an API query filter that can be used to filter tasks
   * @param queryFilters Fetch task query filters
   * @returns An API query filter that can be used to filter tasks
   */
  static queryFiltersToApiFilters({
                                    entitiesId,
                                    assigneeIds,
                                    assigneeIdsToExclude,
                                    createdById,
                                    isAssigned,
                                    taskType
                                  }: FetchTaskQuery) {
    const apiFilters: IJsonApiQuery['filter'] = {};

    if (assigneeIds) {
      apiFilters['assigneeIds'] = inFilter(assigneeIds);
    }
    if (assigneeIdsToExclude) {
      apiFilters['assigneeIds'] = niFilter(assigneeIdsToExclude);
    }
    if (createdById) {
      apiFilters['createdById'] = eqFilter(createdById);
    }
    if (entitiesId) {
      apiFilters['linkedEntityId'] = inFilter(entitiesId);
    }
    if (taskType) {
      apiFilters['taskType'] = inFilter(taskType);
    }
    if (isPresent(isAssigned)) {
      apiFilters['isAssigned'] = eqFilter(isAssigned!);
    }

    return apiFilters;
  }

  static indexTasksByEntity(sortedTasks: UTask[]) {
    return groupBy(sortedTasks, (t: UTask) => t.linkedEntityId);
  }

  static indexTasksByDate(
    sortedTasks: UTask[],
    noDateId: string,
    groupingField: keyof Task = 'dueDate'
  ) {
    return groupBy(sortedTasks, (t: UTask) =>
      groupingField === 'dueDate'
        ? ((t.dueDate ? formatLocalDate(t.dueDate) : noDateId) as string)
        : formatLocalDate(t.createdAt)
    );
  }

  static indexTasksByDateAndEntity(
    sortedTasks: UTask[],
    noDateId: string,
    groupingField: keyof Task = 'dueDate'
  ) {
    if (sortedTasks.length === 0) {
      return {};
    }
    const dates: string[] = [];
    return sortedTasks.reduce((acc: Record<string, UTask[]>, t: UTask) => {
      let formattedDate =
        groupingField === 'dueDate'
          ? t.dueDate
            ? formatLocalDate(t.dueDate)
            : noDateId
          : formatLocalDate(t.createdAt);
      const alreadyExists = dates.includes(formattedDate);

      if (alreadyExists) {
        formattedDate = t.linkedEntityId;
      } else {
        dates.push(formattedDate);
        formattedDate = formattedDate + '|' + t.linkedEntityId;
      }

      if (acc[formattedDate]) {
        acc[formattedDate]?.push(t);
      } else {
        acc[formattedDate] = [t];
      }

      return acc;
    }, {});
  }
}

declare module '@ember/service' {
  interface Registry {
    'task-service': TaskService;
  }
}
